import React from 'react';
import { AssetUrls } from 'config/assets';
import './OffersTabs.css';

export type OffersTab = 'received' | 'suggested' | 'sent';

interface Props {
  activeTab: OffersTab;
  onTabChange: (tab: OffersTab) => void;
  /**
   * Pending offers per tab, and live suggestions for Suggested. A zero renders
   * no pill — same rule the sidebar badge uses, and it keeps "Received 0" from
   * sitting above a list of ten resolved offers. Undefined means "not known",
   * which also renders nothing rather than inventing a zero.
   */
  counts: Partial<Record<OffersTab, number>>;
  /** Right-aligned slot on the tab row; the S/M/L selector on list tabs */
  trailing?: React.ReactNode;
}

/**
 * The Offers page tab row: Received, Suggested, Sent.
 *
 * WHY THIS IS NOT ReceivingStatusTabs. That component is typed to the receiving
 * domain (its props take a ReceivingStatus and its four tabs are hardcoded in
 * the body), renders icons as <img> under a recolouring CSS filter, and has no
 * count rendering at all. None of that generalises to a glyph-icon row with
 * inline counts, so this is a sibling that reproduces the same visual treatment.
 *
 * WHY IT DOES NOT SHARE .status-tab / .tab-icon EITHER. All page CSS in this app
 * is global (static imports in routes/index.tsx), and .status-tab is defined
 * byte-identically in TWO globally imported sheets — ReceivingStatusTabs.css and
 * TradeStatusTabs.css. Importing or editing either would reach /receiving and
 * /sending as well. The values below are therefore duplicated deliberately, and
 * match those sheets exactly: gap 8px, padding 12px 16px, 14px/500, #cccccc
 * inactive, #ffffff active, 2px #7b5ef2 underline.
 */
const OffersTabs: React.FC<Props> = ({ activeTab, onTabChange, counts, trailing }) => {
  // Direction glyphs, extrapolated from the two-arrow Offers nav icon. Text, in
  // the tab's own colour — deliberately NOT icon-receiving/icon-sending, whose
  // box artwork already means physical parcels on /receiving and /sending.
  const tabs: Array<{ key: OffersTab; label: string; icon: React.ReactNode }> = [
    { key: 'received', label: 'Received', icon: <span aria-hidden="true">&#8592;</span> },
    {
      key: 'suggested',
      label: 'Suggested',
      icon: (
        <img
          className="offers-tabrow-icon-img"
          src={AssetUrls.logos.arevakhachIcon}
          alt=""
          aria-hidden="true"
        />
      ),
    },
    { key: 'sent', label: 'Sent', icon: <span aria-hidden="true">&#8594;</span> },
  ];

  return (
    <div className="offers-tabrow" role="tablist" aria-label="Offer direction">
      {tabs.map((tab) => {
        const count = counts[tab.key];
        const isActive = activeTab === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`offers-tabrow-tab${isActive ? ' active' : ''}`}
            onClick={() => onTabChange(tab.key)}
          >
            <span className="offers-tabrow-icon">{tab.icon}</span>
            <span className="offers-tabrow-label">{tab.label}</span>
            {count != null && count > 0 && (
              <span
                className={`offers-tabrow-count offers-tabrow-count--${
                  tab.key === 'suggested' ? 'suggested' : 'neutral'
                }`}
              >
                {count > 99 ? '99+' : count}
              </span>
            )}
          </button>
        );
      })}
      {trailing && <div className="offers-tabrow-trailing">{trailing}</div>}
    </div>
  );
};

export default OffersTabs;
